/**
 * VergleichMatrix — the Soll-Ist comparison matrix + the Offer-Score table (P2 Placement Slice 11,
 * Tab 4b). Offers in rank order; the recommended column is highlighted. Horizontal scroll.
 */

import {
  OFFER_SCORE_DIMENSION_LABELS,
  OFFER_SCORE_DIMENSION_VALUES,
  VERGLEICH_ZEILE_ART_LABELS,
  type ComparisonReportEintrag,
} from '@brokly/shared';
import { Badge } from '../../components/ui/Badge';
import { CarrierLogo } from './CarrierLogo';
import { VergleichAmpel } from './VergleichAmpel';
import { VergleichScoreBadge } from './VergleichScoreBadge';

export function VergleichMatrix({ bericht, empfehlungOfferId }: { bericht: ComparisonReportEintrag; empfehlungOfferId: string | null }) {
  const offers = bericht.offerten; // rank order
  const anyE4 = offers.some((o) => o.e4_flag);

  return (
    <div className="overflow-x-auto" data-testid="vergleich-matrix">
      <table className="w-full min-w-[640px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border text-left">
            <th className="p-2 font-medium">Kriterium</th>
            <th className="p-2 font-medium text-muted-foreground">Soll</th>
            {offers.map((o) => {
              const isEmpf = o.offer_document_id === empfehlungOfferId;
              const isScore = o.offer_document_id === bericht.score_empfehlung_offer_id;
              return (
                <th key={o.offer_document_id} className={`p-2 align-top ${isEmpf ? 'border-t-2 border-t-[var(--status-success-text,#1b7a5e)]' : ''}`} data-offer={o.offer_document_id}>
                  <div className="flex flex-col gap-1">
                    <span className="flex items-center gap-1 font-medium">
                      <CarrierLogo name={o.carrier_name} logoUrl={o.logo_url} size="sm" />
                      {o.carrier_name}
                    </span>
                    <VergleichScoreBadge score={o.score_total} rang={o.rang} e4={o.e4_flag} />
                    {isEmpf && <Badge variant="success">Empfehlung</Badge>}
                    {isScore && !isEmpf && <Badge variant="neutral">Score-Empfehlung</Badge>}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {bericht.zeilen.map((zeile) => (
            <tr key={zeile.key} className="border-b border-border/60">
              <td className="p-2">
                <div className="flex flex-col">
                  <span>{zeile.label}</span>
                  <span className="text-xs text-muted-foreground">{VERGLEICH_ZEILE_ART_LABELS[zeile.art]}</span>
                  {zeile.mindestanforderung && <Badge variant="warning">Mindestanforderung</Badge>}
                </div>
              </td>
              <td className="p-2 text-muted-foreground">{zeile.soll_text}</td>
              {offers.map((o) => {
                const zelle = zeile.zellen[o.offer_document_id];
                return (
                  <td key={o.offer_document_id} className="p-2" title={zelle?.hinweis ?? undefined}>
                    {zelle ? (
                      <span className="flex flex-col gap-0.5">
                        <VergleichAmpel ampel={zelle.ampel} label={zelle.ist_text} size="sm" />
                        {zelle.gap_prozent !== null && <span className="text-xs text-muted-foreground">-{zelle.gap_prozent} %</span>}
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-border font-medium">
            <td className="p-2" colSpan={2}>Offer Score</td>
            {offers.map((o) => (
              <td key={o.offer_document_id} className="p-2">
                <span className="[font-variant-numeric:tabular-nums]">{o.score_total}</span>
                {o.e4_flag && <span className="ml-1 text-xs text-muted-foreground">(vor Abwertung {o.score_vor_abwertung})</span>}
              </td>
            ))}
          </tr>
          {OFFER_SCORE_DIMENSION_VALUES.map((dim) => (
            <tr key={dim} className="text-xs text-muted-foreground">
              <td className="p-2">{OFFER_SCORE_DIMENSION_LABELS[dim]}</td>
              <td className="p-2">{bericht.gewichte[dim]} %</td>
              {offers.map((o) => {
                const sub = o.subscores[dim];
                return (
                  <td key={o.offer_document_id} className="p-2">
                    {sub.quelle === 'default' ? <span className="italic">{sub.wert} (nicht bewertet)</span> : sub.wert}
                  </td>
                );
              })}
            </tr>
          ))}
          {anyE4 && (
            <tr className="text-xs text-muted-foreground">
              <td className="p-2" colSpan={2}>Abwertung (E4)</td>
              {offers.map((o) => (
                <td key={o.offer_document_id} className="p-2">{o.e4_flag ? `-${Math.round((o.score_vor_abwertung - o.score_total) * 10) / 10}` : '-'}</td>
              ))}
            </tr>
          )}
        </tfoot>
      </table>
    </div>
  );
}
